import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from './supabase'
import { Order } from './types'

// ─── Types ───────────────────────────────────────────────────
export interface DailySales {
  date: string
  label: string
  revenue: number
  orders: number
  dayOfWeek: string
}

export interface HourlySales {
  hour: number
  label: string
  orders: number
  revenue: number
}

export interface ChannelBreakdown {
  qr: number
  waiter: number
  other: number
  qrRevenue: number
  waiterRevenue: number
  otherRevenue: number
}

export interface PeakDay {
  date: string
  label: string
  orders: number
  revenue: number
}

export interface PeakHour {
  hour: number
  label: string
  orders: number
  revenue: number
}

export interface TopProduct {
  name: string
  quantity: number
  revenue: number
}

export interface MetricsAnalytics {
  dailySales: DailySales[]
  hourlySales: HourlySales[]
  channelBreakdown: ChannelBreakdown
  peakDays: PeakDay[]
  peakHours: PeakHour[]
  topProducts: TopProduct[]
  totalRevenue: number
  totalOrders: number
  averageTicket: number
}

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

function getOrderChannel(order: Order): 'qr' | 'waiter' | 'other' {
  const src = (order.source || '').toLowerCase()
  if (src.includes('qr') || src.includes('self')) return 'qr'
  if (src.includes('waiter') || src.includes('mesero')) return 'waiter'
  if (src.includes('caja') || src.includes('cashier')) return 'other'
  // If has table_id and dine_in without explicit waiter source → QR
  if (order.table_id && order.delivery_type === 'dine_in') return 'qr'
  if (order.table_id && !src) return 'qr'
  return 'other'
}

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM'
  if (hour < 12) return `${hour} AM`
  if (hour === 12) return '12 PM'
  return `${hour - 12} PM`
}

function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return `${d} ${['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][date.getMonth()]}`
}

function computeAnalytics(
  orders: Order[],
  orderItems: any[],
): MetricsAnalytics {
  const completedOrders = orders.filter(
    (o) =>
      o.status === 'delivered' ||
      o.status === 'ready' ||
      o.status === 'preparing' ||
      o.status === 'accepted' ||
      o.status === 'new',
  )

  // ─── Daily Sales ─────────────────────────────────────────
  const dailyMap = new Map<string, { revenue: number; orders: number }>()
  completedOrders.forEach((o) => {
    const dateKey = o.created_at.slice(0, 10)
    const existing = dailyMap.get(dateKey) || { revenue: 0, orders: 0 }
    existing.revenue += o.total
    existing.orders += 1
    dailyMap.set(dateKey, existing)
  })

  const dailySales: DailySales[] = Array.from(dailyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, data]) => {
      const [y, m, d] = date.split('-').map(Number)
      const dateObj = new Date(y, m - 1, d)
      return {
        date,
        label: formatDateLabel(date),
        revenue: data.revenue,
        orders: data.orders,
        dayOfWeek: DAY_NAMES[dateObj.getDay()],
      }
    })

  // ─── Hourly Sales ────────────────────────────────────────
  const hourlyMap = new Map<number, { orders: number; revenue: number }>()
  for (let h = 0; h < 24; h++) {
    hourlyMap.set(h, { orders: 0, revenue: 0 })
  }
  completedOrders.forEach((o) => {
    const hour = new Date(o.created_at).getHours()
    const existing = hourlyMap.get(hour)!
    existing.orders += 1
    existing.revenue += o.total
  })

  const hourlySales: HourlySales[] = Array.from(hourlyMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([hour, data]) => ({
      hour,
      label: formatHourLabel(hour),
      orders: data.orders,
      revenue: data.revenue,
    }))

  // ─── Channel Breakdown ───────────────────────────────────
  const channelBreakdown: ChannelBreakdown = {
    qr: 0,
    waiter: 0,
    other: 0,
    qrRevenue: 0,
    waiterRevenue: 0,
    otherRevenue: 0,
  }
  completedOrders.forEach((o) => {
    const ch = getOrderChannel(o)
    channelBreakdown[ch] += 1
    if (ch === 'qr') channelBreakdown.qrRevenue += o.total
    else if (ch === 'waiter') channelBreakdown.waiterRevenue += o.total
    else channelBreakdown.otherRevenue += o.total
  })

  // ─── Peak Days ───────────────────────────────────────────
  const peakDays: PeakDay[] = Array.from(dailyMap.entries())
    .sort(([, a], [, b]) => b.orders - a.orders)
    .slice(0, 3)
    .map(([date, data]) => ({
      date,
      label: formatDateLabel(date),
      orders: data.orders,
      revenue: data.revenue,
    }))

  // ─── Peak Hours ──────────────────────────────────────────
  const peakHours: PeakHour[] = Array.from(hourlyMap.entries())
    .filter(([, data]) => data.orders > 0)
    .sort(([, a], [, b]) => b.orders - a.orders)
    .slice(0, 3)
    .map(([hour, data]) => ({
      hour,
      label: formatHourLabel(hour),
      orders: data.orders,
      revenue: data.revenue,
    }))

  // ─── Top Products ────────────────────────────────────────
  const productMap = new Map<string, { quantity: number; revenue: number }>()
  orderItems.forEach((item: any) => {
    const name = item.item_name_snapshot || 'Desconocido'
    const existing = productMap.get(name) || { quantity: 0, revenue: 0 }
    existing.quantity += item.quantity || 1
    existing.revenue += item.line_total || 0
    productMap.set(name, existing)
  })

  const topProducts: TopProduct[] = Array.from(productMap.entries())
    .sort(([, a], [, b]) => b.quantity - a.quantity)
    .slice(0, 10)
    .map(([name, data]) => ({
      name,
      quantity: data.quantity,
      revenue: data.revenue,
    }))

  // ─── Totals ──────────────────────────────────────────────
  const totalRevenue = completedOrders.reduce((sum, o) => sum + o.total, 0)
  const totalOrders = completedOrders.length
  const averageTicket = totalOrders > 0 ? totalRevenue / totalOrders : 0

  return {
    dailySales,
    hourlySales,
    channelBreakdown,
    peakDays,
    peakHours,
    topProducts,
    totalRevenue,
    totalOrders,
    averageTicket,
  }
}

// ─── Main Hook ─────────────────────────────────────────────
export function useMetricsDateRange(
  restaurantId: string | undefined,
  startDate: string,
  endDate: string,
) {
  const [orders, setOrders] = useState<Order[]>([])
  const [orderItems, setOrderItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!restaurantId || !startDate || !endDate) return
    setLoading(true)
    try {
      // Fetch all orders in date range (with safety limit)
      const endDatePlusOne = new Date(endDate)
      endDatePlusOne.setDate(endDatePlusOne.getDate() + 1)
      const endDateStr = endDatePlusOne.toISOString().slice(0, 10)

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .gte('created_at', `${startDate}T00:00:00`)
        .lt('created_at', `${endDateStr}T00:00:00`)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: true })

      if (ordersError) throw ordersError

      const fetchedOrders = ordersData || []
      setOrders(fetchedOrders)

      // Fetch order items for these orders (in batches if needed)
      if (fetchedOrders.length > 0) {
        const orderIds = fetchedOrders.map((o) => o.id)
        const batchSize = 200
        let allItems: any[] = []

        for (let i = 0; i < orderIds.length; i += batchSize) {
          const batch = orderIds.slice(i, i + batchSize)
          const { data: itemsData } = await supabase
            .from('order_items')
            .select('*')
            .in('order_id', batch)
          if (itemsData) allItems = allItems.concat(itemsData)
        }
        setOrderItems(allItems)
      } else {
        setOrderItems([])
      }
    } catch (error) {
      console.error('[NOIZA] Error fetching metrics data:', error)
      setOrders([])
      setOrderItems([])
    } finally {
      setLoading(false)
    }
  }, [restaurantId, startDate, endDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const analytics = useMemo(
    () => computeAnalytics(orders, orderItems),
    [orders, orderItems],
  )

  return { orders, orderItems, analytics, loading, refetch: fetchData }
}

// ─── CSV Export ────────────────────────────────────────────
export function generateMetricsCsv(
  analytics: MetricsAnalytics,
  orders: Order[],
): string {
  const lines: string[] = []

  // Summary
  lines.push('=== RESUMEN GENERAL ===')
  lines.push('Métrica,Valor')
  lines.push(`Total Órdenes,${analytics.totalOrders}`)
  lines.push(`Ingresos Totales,${analytics.totalRevenue}`)
  lines.push(`Ticket Promedio,${Math.round(analytics.averageTicket)}`)
  lines.push(`Pedidos QR (autoservicio),${analytics.channelBreakdown.qr}`)
  lines.push(`Pedidos Mesero,${analytics.channelBreakdown.waiter}`)
  lines.push(`Pedidos Otros,${analytics.channelBreakdown.other}`)
  lines.push('')

  // Daily Sales
  lines.push('=== VENTAS POR DÍA ===')
  lines.push('Fecha,Día,Órdenes,Ingresos')
  analytics.dailySales.forEach((d) => {
    lines.push(`${d.date},${d.dayOfWeek},${d.orders},${d.revenue}`)
  })
  lines.push('')

  // Hourly Sales
  lines.push('=== VENTAS POR HORA ===')
  lines.push('Hora,Órdenes,Ingresos')
  analytics.hourlySales.forEach((h) => {
    lines.push(`${h.label},${h.orders},${h.revenue}`)
  })
  lines.push('')

  // Peak Days
  lines.push('=== DÍAS PICO ===')
  lines.push('Fecha,Órdenes,Ingresos')
  analytics.peakDays.forEach((p) => {
    lines.push(`${p.date},${p.orders},${p.revenue}`)
  })
  lines.push('')

  // Peak Hours
  lines.push('=== HORAS PICO ===')
  lines.push('Hora,Órdenes,Ingresos')
  analytics.peakHours.forEach((p) => {
    lines.push(`${p.label},${p.orders},${p.revenue}`)
  })
  lines.push('')

  // Top Products
  lines.push('=== PRODUCTOS MÁS VENDIDOS ===')
  lines.push('Producto,Cantidad,Ingresos')
  analytics.topProducts.forEach((p) => {
    lines.push(`"${p.name}",${p.quantity},${p.revenue}`)
  })
  lines.push('')

  // Raw Orders
  lines.push('=== DETALLE DE ÓRDENES ===')
  lines.push('Orden,Fecha,Estado,Cliente,Tipo,Canal,Total')
  orders.forEach((o) => {
    const channel = getOrderChannel(o)
    lines.push(
      `${o.order_number},${o.created_at},${o.status},"${o.customer_name || ''}",${o.delivery_type},${channel},${o.total}`,
    )
  })

  return lines.join('\n')
}

export function downloadCsv(csvContent: string, filename: string) {
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
